import { useState, useEffect, useRef } from 'react';
import styles from './LongCalculationModal.module.css';
import type { SolverJobStatus } from '../utils/solverJobClient';

/** Anzeige-Zustand, den describeJob aus dem Job-Status ableitet. */
interface JobView {
  indeterminate: boolean;
  percent: number;
  phase: string;
  note?: string;
  solutions?: number;
  remainingPercent?: number;
}

// Phase 1: 0–15s (sachlich)
const MESSAGES_PHASE1 = [
  "Studenten werden verteilt ...",
  "Verfügbarkeiten werden abgeglichen ...",
  "Unterrichtszeiten werden sortiert ...",
  "Räume und Zeiten werden kombiniert ...",
  "Mögliche Stundenpläne werden berechnet ...",
  "Überschneidungen werden geprüft ...",
  "Passende Zeitfenster werden gesucht ...",
  "Prioritäten werden berücksichtigt ...",
  "Konflikte werden aufgelöst ...",
  "Lehrkräfte und Studierende werden koordiniert ...",
  "Belegte Zeiten werden umschifft ...",
  "Freie Slots werden eingesammelt ...",
  "Mögliche Kombinationen laufen Probe ...",
  "Der Stundenplan nimmt erste Formen an ...",
  "Kollisionen werden enttarnt ...",
  "Zeitfenster werden neu zusammengesetzt ...",
  "Die beste Verteilung wird gesucht ...",
  "Termine werden vorsichtig eingerückt ...",
];

// Phase 2: 15–45s (leicht augenzwinkernd)
const MESSAGES_PHASE2 = [
  "Varianten werden abgewogen ...",
  "Die sinnvollste Reihenfolge wird gesucht ...",
  "Freie Lücken werden neu bewertet ...",
  "Ein möglichst stimmiger Ablauf entsteht ...",
  "Unpraktische Kombinationen werden aussortiert ...",
  "Engpässe werden neu verteilt ...",
  "Feinabstimmungen laufen ...",
  "Die Balance zwischen Wünschen und Möglichkeiten wird geprüft ...",
  "Ein passendes Gesamtbild formt sich ...",
  "Letzte Konfliktzonen werden untersucht ...",
  "Ein paar Termine leisten noch Widerstand ...",
  "Der Dienstag zeigt sich heute kompliziert ...",
  "Freistunden wollen sinnvoll platziert werden ...",
  "Der Plan wird neu ausbalanciert ...",
  "Noch etwas Feinarbeit an den Übergängen ...",
  "Die nächste Variante sieht schon vielversprechend aus ...",
  "Ein eleganterer Ablauf wird getestet ...",
  "Das System denkt kurz um die Ecke ...",
];

// Phase 3: 45–90s (humorvoll)
const MESSAGES_PHASE3 = [
  "Kleine Wunder der Koordination geschehen ...",
  "Der Stundenplan atmet kurz durch ...",
  "Verlorene Viertelstunden werden wieder eingefangen ...",
  "Ein paar hartnäckige Kollisionen geben noch nicht auf ...",
  "Das System verhandelt mit dem Dienstagvormittag ...",
  "Freistunden werden kritisch beäugt ...",
  "Die Logik schiebt noch ein paar Termine zurecht ...",
  "Ein fast perfektes Zeitgefüge entsteht ...",
  "Letzte Kompromisse werden geschlossen ...",
  "Der Plan wird langsam bühnentauglich ...",
  "Der Algorithmus räumt hinter dem Chaos auf ...",
  "Ein paar hartnäckige Überschneidungen werden höflich entfernt ...",
  "Die Zeitachsen werden entwirrt ...",
  "Montag, 10 Uhr, bleibt ein schwieriger Verhandlungspartner ...",
  "Der Stundenplan wird gerade in Form massiert ...",
  "Ein besonders schönes Zeitfenster wird reserviert ...",
  "Die letzte Runde der Terminakrobatik läuft ...",
  "Fast da. Der Plan richtet noch seine Krawatte ...",
];

// Phase 4: 90–110s (absurd-lustig)
const MESSAGES_PHASE4 = [
  "Die Stundenplan-Galaxie wird neu geordnet ...",
  "Montag und Mittwoch diskutieren ihre Zuständigkeiten ...",
  "Ein rebellisches Zeitfenster wird überzeugt mitzumachen ...",
  "Die Matrix der Möglichkeiten flimmert ...",
  "Noch eine elegante Schleife durch alle Optionen ...",
  "Ein paar Termine versuchen, sich zu verstecken ...",
  "Der Algorithmus jongliert mit Verfügbarkeiten ...",
  "Aus Chaos wird Struktur. Meistens jedenfalls ...",
  "Die optimale Variante winkt schon aus der Ferne ...",
  "Fast geschafft. Nur noch ein paar Raum-Zeit-Kapriolen ...",
];

// Phase 5: 110s+ (Finale)
const MESSAGES_PHASE5 = [
  "Der Stundenplan wird mit sanfter Entschlossenheit gezähmt ...",
  "Eine besonders störrische Überschneidung wird in Rente geschickt ...",
  "Die letzten freien Slots werden poetisch verwaltet ...",
  "Ein einsamer Termin findet endlich seinen Platz im Universum ...",
  "Der Plan erhält gerade seinen Feinschliff und etwas Würde ...",
  "Die Zeit selbst wird kurz um Erlaubnis gefragt ...",
  "Ein paar Termine werden mit diplomatischem Geschick versöhnt ...",
  "Die finale Ordnung setzt sich gegen das kreative Durcheinander durch ...",
  "Noch ein letzter Blick auf das große Ganze ...",
  "Der Stundenplan wird gleich aus dem Backstage-Bereich geholt ...",
];

const PHASES = [
  { minSec: 0,   interval: 2.2, messages: MESSAGES_PHASE1 },
  { minSec: 15,  interval: 2.5, messages: MESSAGES_PHASE2 },
  { minSec: 45,  interval: 2.8, messages: MESSAGES_PHASE3 },
  { minSec: 90,  interval: 3.0, messages: MESSAGES_PHASE4 },
  { minSec: 110, interval: 3.0, messages: MESSAGES_PHASE5 },
];

function getPhaseIndex(sec: number): number {
  for (let i = PHASES.length - 1; i >= 0; i--) {
    if (sec >= PHASES[i].minSec) return i;
  }
  return 0;
}

function pickRandom(arr: string[], exclude: string): string {
  if (arr.length <= 1) return arr[0] || '';
  let pick: string;
  do {
    pick = arr[Math.floor(Math.random() * arr.length)];
  } while (pick === exclude);
  return pick;
}

/**
 * Übersetzt den Job-Status in Phase + Kennzahlen für die Anzeige.
 *
 * Milestone 6: Der Balken zeigt ECHTEN Solver-Fortschritt (Gap zur unteren
 * Schranke) statt des früheren Fake-Werts (elapsed/120s). Solange keine Lösung
 * gefunden ist, gibt es bewusst keinen bezifferbaren Fortschritt — dann läuft
 * der Balken unbestimmt.
 */
function describeJob(jobStatus: SolverJobStatus | null | undefined): JobView {
  if (!jobStatus) {
    return { indeterminate: true, percent: 0, phase: 'Berechnung wird gestartet' };
  }

  if (jobStatus.status === 'queued') {
    const position = jobStatus.queue_position || 1;
    return {
      indeterminate: true,
      percent: 0,
      phase: 'In der Warteschlange',
      note: position > 1 ? `Position ${position}` : 'gleich geht es los',
    };
  }

  const progress = jobStatus.progress || {};
  const solutions = progress.solutions || 0;

  if (solutions === 0) {
    return {
      indeterminate: true,
      percent: Math.round((progress.progress || 0) * 100),
      phase: 'Suche erste gültige Lösung',
    };
  }

  return {
    indeterminate: false,
    percent: Math.round((progress.progress || 0) * 100),
    phase: 'Lösung gefunden – wird optimiert',
    solutions,
    remainingPercent: Math.max(1, Math.round((progress.gap || 0) * 100)),
  };
}

interface LongCalculationModalProps {
  isOpen: boolean;
  showPatienceHint?: boolean;
  elapsedSeconds: number;
  jobStatus?: SolverJobStatus | null;
  /** Optimierung vorzeitig beenden und den besten bisherigen Plan übernehmen. */
  onAcceptBest?: (() => void) | null;
  onDiscard?: (() => void) | null;
  cancelling?: boolean;
}

export default function LongCalculationModal({ isOpen, showPatienceHint, elapsedSeconds, jobStatus, onAcceptBest, onDiscard, cancelling }: LongCalculationModalProps) {
  const [message, setMessage] = useState('');
  const [msgKey, setMsgKey] = useState(0);
  const messageRef = useRef('');
  const elapsedRef = useRef(0);

  // Keep elapsed accessible to the timer without re-triggering it
  useEffect(() => { elapsedRef.current = elapsedSeconds; }, [elapsedSeconds]);

  // Single timer drives everything
  useEffect(() => {
    if (!isOpen) {
      messageRef.current = '';
      setMessage('');
      return;
    }
    const show = () => {
      const phase = PHASES[getPhaseIndex(elapsedRef.current)];
      const next = pickRandom(phase.messages, messageRef.current);
      messageRef.current = next;
      setMessage(next);
      setMsgKey(k => k + 1);
    };
    show(); // first message immediately
    let id: ReturnType<typeof setTimeout>;
    const tick = () => {
      show();
      id = setTimeout(tick, 5000 + Math.random() * 3000);
    };
    id = setTimeout(tick, 5000 + Math.random() * 3000);
    return () => clearTimeout(id);
  }, [isOpen]);

  if (!isOpen) return null;

  const job = describeJob(jobStatus);
  // Sobald ein gültiger Plan existiert, wird aus "Abbrechen" ein "Übernehmen":
  // Der Solver liefert bei StopSearch() den besten bisher gefundenen Plan.
  const hasSolution = (job.solutions || 0) > 0;
  const minutes = Math.floor(elapsedSeconds / 60);
  const seconds = elapsedSeconds % 60;
  const formattedElapsed = `${minutes}:${String(seconds).padStart(2, '0')}`;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-live="polite">
        <div className={styles.header}>
          <div className={styles.spinner} aria-hidden="true" />
          <div>
            <h2>Stundenplan wird erstellt</h2>
            <p className={styles.subline}>Bitte Fenster nicht schließen und die Seite nicht neu laden.</p>
          </div>
        </div>

        {/* Sachlicher Solver-Status: Phase, Balken, Kennzahlen — alles in einer Karte */}
        <div className={styles.statusCard}>
          <div className={styles.phaseRow}>
            <span className={styles.phaseText}>{job.phase}</span>
            {job.note && <span className={styles.phaseNote}>{job.note}</span>}
          </div>

          <div
            className={styles.progressTrack}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={job.indeterminate ? undefined : job.percent}
          >
            <div
              className={`${styles.progressFill} ${job.indeterminate ? styles.progressFillPending : ''}`}
              style={job.indeterminate ? undefined : { width: `${job.percent}%` }}
            />
          </div>

          <dl className={styles.stats}>
            <div className={styles.stat}>
              <dt>Verstrichene Zeit</dt>
              <dd>{formattedElapsed} <span className={styles.statMuted}>/ 2:00</span></dd>
            </div>
            <div className={styles.stat}>
              <dt>Gefundene Pläne</dt>
              <dd>{job.solutions ?? '–'}</dd>
            </div>
            <div
              className={styles.stat}
              title="So viel besser könnte der Plan im besten Fall noch werden. Sinkt der Wert Richtung 0 %, ist der aktuelle Plan praktisch optimal."
            >
              <dt>Verbesserungspotenzial</dt>
              <dd>{job.remainingPercent != null ? `${job.remainingPercent} %` : '–'}</dd>
            </div>
          </dl>
        </div>

        {/* Die lockeren Sprüche — bewusst die einzige Stelle mit Farbwechsel */}
        <p className={styles.funMessage} key={msgKey}>{message}</p>

        {showPatienceHint && (
          <p className={styles.patienceHint}>
            Der Solver nutzt die verbleibende Zeit, um die bestmögliche Verteilung zu finden.
          </p>
        )}

        {/* Sobald ein gültiger Plan existiert, gibt es zwei Wege: den Plan
            übernehmen (Optimierung vorzeitig beenden) oder ganz verwerfen. */}
        <div className={styles.actions}>
          {hasSolution && onAcceptBest && (
            <button
              type="button"
              className={styles.acceptButton}
              onClick={onAcceptBest}
              disabled={cancelling}
            >
              {cancelling ? 'Wird beendet …' : 'Beste Lösung übernehmen'}
            </button>
          )}
          {onDiscard && (
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onDiscard}
              disabled={cancelling}
            >
              {hasSolution ? 'Abbrechen (verwerfen)' : 'Berechnung abbrechen'}
            </button>
          )}
        </div>
        {hasSolution && !cancelling && (
          <p className={styles.acceptHint}>
            Der Plan ist bereits vollständig — die restliche Zeit macht ihn nur noch kompakter.
          </p>
        )}
      </div>
    </div>
  );
}
